import { useState, useMemo } from 'react';
import { ILogisticServiceProduct } from 'types/services';
import { PRODUCT_CATEGORY } from 'types/category';
import { CURRENCIES } from 'types/bussines';
import { NudosButton } from 'components/NudosComponents';
import BordOneToneIcon from 'components/NudosComponents/BordOneToneIcon/BordOneToneIcon';
import NudosStaticAlertInComponent from 'components/NudosComponents/NudosStaticAlertInComponent/NudosStaticAlertInComponent';
import { displayErrorNotification, displaySuccessNotification } from 'utils/displayNudosStandardNotifications';
import {
  prepareUpdatedPrices as prepareUpdatedPricesUtil,
  IUpdatedPrice,
  PRICE_TYPE
} from 'utils/logisticServiceEdition';
import BulkPriceEditionRow from './BulkPriceEditionRow/BulkPriceEditionRow';
import './BulkPriceEditionModalContent.scss';

interface IBulkPriceEditionModalContentProps {
  selectedProducts: ILogisticServiceProduct[];
  currency: string;
  exchangeRate?: number;
  productIndexMap: Map<string | number, { productIndex: number; priceIndex: number }>;
  onUpdateFormPrices: (updatedPrices: IUpdatedPrice[]) => Promise<void>;
  onClose: () => void;
}

interface ILogisticServiceType {
  id: number;
  name: string;
  count: number;
  type: PRICE_TYPE;
}

interface IServicePrice {
  priceUsd: string;
  totalValueLocal?: string;
  exchangeRate?: number;
}

type TProductCategory = 'accessories' | 'nonAccessories';

const BulkPriceEditionModalContent = ({
  selectedProducts,
  currency,
  exchangeRate,
  productIndexMap,
  onUpdateFormPrices,
  onClose = () => {}
}: IBulkPriceEditionModalContentProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [pricesByServiceType, setPricesByServiceType] = useState<{
    [key: string]: IServicePrice;
  } | null>(null);

  const getPriceKey = (category: TProductCategory, serviceTypeId: number, type: PRICE_TYPE): string => {
    return `${category}_${type}_${serviceTypeId}`;
  };

  const { accessoriesProducts, nonAccessoriesProducts } = useMemo(() => {
    const accessories: ILogisticServiceProduct[] = [];
    const nonAccessories: ILogisticServiceProduct[] = [];

    selectedProducts.forEach(product => {
      const category = product?.product?.category;
      if (category === PRODUCT_CATEGORY.ACCESSORIES) {
        accessories.push(product);
      } else {
        nonAccessories.push(product);
      }
    });

    return {
      accessoriesProducts: accessories,
      nonAccessoriesProducts: nonAccessories
    };
  }, [selectedProducts]);

  const getUniqueLogisticServices = (products: ILogisticServiceProduct[]): ILogisticServiceType[] => {
    const servicesMap = new Map<string, ILogisticServiceType>();

    products.forEach(product => {
      const prices = product?.logisticServiceProductPrices || [];

      prices.forEach(price => {
        const serviceTypeId = price?.logisticServiceType?.id || price?.logisticServiceTypeId;
        const configClassId = price?.configClass?.id || price?.configClassId;

        if (serviceTypeId) {
          const key = `service_${serviceTypeId}`;
          const existing = servicesMap.get(key);
          if (existing) {
            existing.count += 1;
          } else {
            const firstPrice = prices?.find(
              p => p?.logisticServiceType?.id === serviceTypeId || p?.logisticServiceTypeId === serviceTypeId
            );
            servicesMap.set(key, {
              id: serviceTypeId,
              name: firstPrice?.logisticServiceType?.name || '',
              count: 1,
              type: PRICE_TYPE.SERVICE
            });
          }
        }

        if (configClassId) {
          const key = `config_${configClassId}`;
          const existing = servicesMap.get(key);
          if (existing) {
            existing.count += 1;
          } else {
            const firstPrice = prices?.find(
              p => p?.configClass?.id === configClassId || p?.configClassId === configClassId
            );
            servicesMap.set(key, {
              id: configClassId,
              name: firstPrice?.configClass?.name || '',
              count: 1,
              type: PRICE_TYPE.CONFIG
            });
          }
        }
      });
    });

    return Array.from(servicesMap.values());
  };

  const accessoriesLogisticServices = useMemo(
    () => getUniqueLogisticServices(accessoriesProducts),
    [accessoriesProducts]
  );

  const nonAccessoriesLogisticServices = useMemo(
    () => getUniqueLogisticServices(nonAccessoriesProducts),
    [nonAccessoriesProducts]
  );

  const handlePriceChange = (
    category: TProductCategory,
    serviceTypeId: number,
    price: IServicePrice,
    type: PRICE_TYPE
  ) => {
    const key = getPriceKey(category, serviceTypeId, type);
    setPricesByServiceType(prev => ({
      ...(prev || {}),
      [key]: price
    }));
  };

  const isValidServicePrice = (price: IServicePrice | undefined): boolean => {
    if (!price || !price?.priceUsd) return false;

    const numericPriceUsd = Number(price?.priceUsd?.replace(/[^0-9.]/g, '')) || 0;
    if (numericPriceUsd <= 0) return false;

    if (currency === CURRENCIES.ARS || currency === CURRENCIES.BRL) {
      if (!price?.totalValueLocal) return false;
      const numericLocalPrice = Number(price?.totalValueLocal?.replace(/[^0-9.]/g, '')) || 0;
      if (numericLocalPrice <= 0) return false;
    }

    return true;
  };

  const isFormValid = (): boolean => {
    if (!selectedProducts?.length || !pricesByServiceType) return false;

    const allNonAccessoriesHavePrice = nonAccessoriesLogisticServices.every(service => {
      const priceKey = getPriceKey('nonAccessories', service?.id, service?.type);
      return isValidServicePrice(pricesByServiceType[priceKey]);
    });

    const allAccessoriesHavePrice =
      !accessoriesLogisticServices?.length ||
      accessoriesLogisticServices.every(service => {
        const priceKey = getPriceKey('accessories', service?.id, service?.type);
        return isValidServicePrice(pricesByServiceType[priceKey]);
      });

    return allNonAccessoriesHavePrice && allAccessoriesHavePrice;
  };

  const prepareUpdatedPrices = (): IUpdatedPrice[] => {
    return prepareUpdatedPricesUtil(selectedProducts, pricesByServiceType || {}, productIndexMap, exchangeRate);
  };

  const handleSubmit = async () => {
    if (!isFormValid()) return;

    setIsLoading(true);
    try {
      const updatedPrices = prepareUpdatedPrices();
      await onUpdateFormPrices(updatedPrices);
      displaySuccessNotification({
        customJSXMessage: <>Precios editados correctamente, recuerda guardar los cambios para aplicarlos al servicio.</>
      });
    } catch (error) {
      displayErrorNotification({
        customJSXMessage: <>No se pudo actualizar el precio de las herramientas</>
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bulkPriceEditionModalContent">
      <p className="subtitle">
        <strong>Actualiza el precio de las herramientas seleccionadas</strong>
      </p>
      <div className="priceContainer">
        <div className="content">
          {!!accessoriesLogisticServices?.length && <span className="priceTitle">Precio general</span>}
          {nonAccessoriesLogisticServices?.map(service => {
            const priceKey = getPriceKey('nonAccessories', service?.id, service?.type);
            return (
              <BulkPriceEditionRow
                key={`${service?.type}_${service?.id}`}
                currency={currency}
                exchangeRate={exchangeRate}
                totalItems={nonAccessoriesProducts?.length}
                service={service}
                price={pricesByServiceType?.[priceKey]}
                onPriceChange={(serviceTypeId, price) =>
                  handlePriceChange('nonAccessories', serviceTypeId, price, service?.type)
                }
              />
            );
          })}
          {!!accessoriesLogisticServices?.length && (
            <>
              <div className="divider"></div>
              <span className="priceTitle">Precio de accesorios</span>
              {accessoriesLogisticServices?.map(service => {
                const priceKey = getPriceKey('accessories', service?.id, service?.type);
                return (
                  <BulkPriceEditionRow
                    key={`${service?.type}_${service?.id}`}
                    currency={currency}
                    exchangeRate={exchangeRate}
                    totalItems={accessoriesProducts?.length}
                    service={service}
                    price={pricesByServiceType?.[priceKey]}
                    onPriceChange={(serviceTypeId, price) =>
                      handlePriceChange('accessories', serviceTypeId, price, service?.type)
                    }
                  />
                );
              })}
            </>
          )}
        </div>
        <NudosStaticAlertInComponent
          customClassname="alertWarning"
          alertOptions={{
            icon: <BordOneToneIcon variant="triangleAlertCircle" customWidth="26px" />,
            title:
              'Este precio se aplicará a todas las herramientas seleccionadas, sin importar su marca, modelo o características.'
          }}
        />
      </div>

      <div className="modalActions">
        <NudosButton
          customClassName="cancelButton"
          buttonText="Cancelar"
          colorPalette="secondary"
          handleClick={onClose}
        />
        <NudosButton
          customClassName="saveButton"
          buttonText="Aplicar precio"
          colorPalette="primary"
          handleClick={handleSubmit}
          isButtonDisabled={!isFormValid()}
          isButtonLoading={isLoading}
        />
      </div>
    </div>
  );
};

export default BulkPriceEditionModalContent;
